// filepath: /Users/evan/Documents/gnoknews/src/app/pages/settings/settings.page.ts
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { IonicModule, AlertController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { User } from '../../entities/user.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LANGUAGE_MAPPINGS, NATIVE_LANGUAGES } from './language-mapping';
import { TourService } from 'src/app/services/tour.service';
import { first } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [IonicModule, CommonModule, RouterModule, FormsModule, HttpClientModule, TranslateModule],
  templateUrl: './settings.page.html',
  styleUrls: ['./settings.page.scss'],
})
export class SettingsPage implements OnInit {
  user: User | null = null;
  loading = true;
  changesMade = false;
  errorMessage: string | null = null;
  nativeLanguages: { code: string, name: string }[] = NATIVE_LANGUAGES;

  freshUser = false; // Flag to indicate if the user is new

  targetLanguages: { code: string, name: string }[] = [];

  fluencyLevels = [
    { value: 1, label: 'SETTINGS.BEGINNER' },
    { value: 2, label: 'SETTINGS.INTERMEDIATE' },
    { value: 3, label: 'SETTINGS.ADVANCED' }
  ];

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private translate: TranslateService,
    private router: Router,
    private tourService: TourService,
    private alertController: AlertController
  ) {}

  ngOnInit() {
    document.title = 'Settings';
    this.authService.currentUser$.subscribe(user => {
      this.user = user;
      this.loading = false;
      if (this.user) {
        if (!this.user.targetLanguage) {
          this.freshUser = true;
          this.user.targetLanguage = '';
          this.user.level = {};
        }
        this.onNativeLanguageChange();
      }
    });
  }

  onNativeLanguageChange() {
    const selectedLanguage = NATIVE_LANGUAGES.find(lang => lang.code === this.user?.nativeLanguage);
    if (selectedLanguage) {
      this.translate.use(selectedLanguage.code);
      this.targetLanguages = LANGUAGE_MAPPINGS[selectedLanguage.code] || [];
    }
    this.errorMessage = null;
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }

  async applyChanges() {
    if (this.user) {
      if (this.user.nativeLanguage == this.user.targetLanguage) {
        this.errorMessage = this.translate.instant('SETTINGS.EMPTY_TARGET_LANG');
        return;
      }
      else if(!this.user.level[this.user.targetLanguage]) {
        this.errorMessage = this.translate.instant('SETTINGS.EMPTY_PROFICIENCY');
        return;
      }
      this.errorMessage = null;
      await this.userService.applyChanges(this.user);
      this.changesMade = false;
      if (!this.user.completedTour) {
        this.router.navigate(['/welcome']);
      } else {
        this.router.navigate(['/news']);
      }
    }
  }

  async signOut() {
    await this.authService.signOut();
    await this.authService.authStateResolved$.pipe(first(resolved => resolved === true)).toPromise();
    this.router.navigate(['/login']);
  }

  onFieldChange() {
    this.changesMade = true;
  }

  startTour() {
    if (this.user) {
      this.tourService.startTour(this.user.targetLanguage);
    }
  }

  async deleteAccount() {
    const alert = await this.alertController.create({
      header: this.translate.instant('SETTINGS.DELETE_ACCOUNT_TITLE'),
      message: this.translate.instant('SETTINGS.DELETE_ACCOUNT_MESSAGE'),
      buttons: [
        {
          text: this.translate.instant('SETTINGS.CANCEL'),
          role: 'cancel',
          cssClass: 'secondary'
        },
        {
          text: this.translate.instant('SETTINGS.DELETE'),
          cssClass: 'danger',
          handler: async () => {
            try {
              await this.userService.deleteUserAccount();
              // Sign out the user after deletion
              await this.authService.signOut();
              // Navigate to login page
              this.router.navigate(['/login']);
            } catch (error) {
              console.error('Error deleting account:', error);
              // Show error alert
              const errorAlert = await this.alertController.create({
                header: this.translate.instant('SETTINGS.ERROR'),
                message: this.translate.instant('SETTINGS.DELETE_ACCOUNT_ERROR'),
                buttons: [this.translate.instant('SETTINGS.OK')]
              });
              await errorAlert.present();
            }
          }
        }
      ]
    });

    await alert.present();
  }

  getPrivacyUrl(): string {
    const nativeLang = this.user?.nativeLanguage || 'en';
    return `https://about.ezknews.com/support/${nativeLang}`;
  }

  goToReadingSettings(): void {
    this.router.navigate(['/setup']);
  }

  goToAudioSettings(): void {
    this.router.navigate(['/audio-setup']);
  }
}